import FormField from '@components/Common/FormField';
import PhoneInput from '@components/Common/PhoneInput';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useState } from 'react';
import CredentialRow from '@components/Employers/CredentialRow';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called after a successful create so the list can pick up the new row. */
  onCreated: () => void;
}

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  city: string;
  isPremium: boolean;
  premiumTier: string;
}

interface Credentials {
  email: string;
  password: string;
  employerId: string;
  isPremium: boolean;
  premiumError: string | null;
}

const EMPTY_FORM: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  city: '',
  isPremium: false,
  premiumTier: '',
};

/**
 * Create an employer account from the Control Center, for Managed clients we onboard directly
 * rather than have them sign up.
 *
 * Two-step by design: the form submits, then the dialog swaps to a credentials panel. The
 * generated password is returned exactly once by POST /api/employers and is never retrievable
 * again, so closing the dialog before copying it means issuing a password reset. The close
 * affordances on the credentials step are labelled to make that obvious.
 */
const EmployerCreateDialog: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [credentials, setCredentials] = useState<Credentials | null>(null);

  const handleChange = (name: string, value: any) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleClose = () => {
    // Never carry a previous account's password into the next open.
    setForm(EMPTY_FORM);
    setError('');
    setCredentials(null);
    setSaving(false);
    onClose();
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');
    try {
      const { data } = await axios.post('/api/employers', {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        city: form.city,
        isPremium: form.isPremium,
        premiumTier: form.isPremium ? form.premiumTier : undefined,
      });
      setCredentials({
        email: data.employer?.personalDetails?.email ?? form.email,
        password: data.password,
        employerId: data.employer?._id ?? '',
        isPremium: !!data.employer?.isPremium,
        premiumError: data.premiumError ?? null,
      });
      onCreated();
    } catch (err: any) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          'Could not create the employer'
      );
    } finally {
      setSaving(false);
    }
  };

  const canSubmit =
    !!form.firstName.trim() && !!form.lastName.trim() && !!form.email.trim();

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle>
        {credentials ? 'Employer created' : 'Create Employer'}
      </DialogTitle>

      {credentials ? (
        <>
          <DialogContent>
            <Alert icon={<WarningAmberIcon />} severity="warning" sx={{ mb: 2 }}>
              This password is shown once and is not stored anywhere. Copy it
              now and share it with the employer. If you lose it, they will need
              a password reset.
            </Alert>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <CredentialRow label="Email" value={credentials.email} />
              <CredentialRow label="Password" value={credentials.password} />
            </Box>

            {credentials.premiumError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                The account was created, but marking it a Premium Hiring client failed:{' '}
                {credentials.premiumError}. Enable Premium Hiring on the employer&apos;s
                detail page.
              </Alert>
            )}

            <Typography
              sx={{ color: 'var(--color-text-muted)', mt: 2 }}
              variant="body2"
            >
              {credentials.isPremium
                ? 'Marked as a Premium Hiring client. Assign a dedicated HR on the HR page. They can post jobs without buying a plan.'
                : 'A self-serve employer account. They can subscribe to a plan themselves, or you can enable Premium Hiring on their detail page.'}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={handleClose}>
              I have copied the password
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent>
            <Typography
              sx={{ color: 'var(--color-text-muted)', mb: 2 }}
              variant="body2"
            >
              Creates a login for an employer we onboard directly. The account
              is email-verified, and a password is generated for you to share.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormField
                  required
                  label="First Name"
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormField
                  required
                  label="Last Name"
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormField
                  required
                  helperText="They log in with this address."
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <PhoneInput
                  required={false}
                  fullWidth
                  label="Phone"
                  value={form.phone}
                  onChange={(val) => handleChange('phone', val)}
                />
              </Grid>
              {/*
                * PersonalDetailsCreateInput has taken a city all along; the dialog never asked for
                * one, so an account created here had none until somebody opened it and typed it in.
                * It is what the employer lists and filters group by.
                */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormField
                  required={false}
                  label="City"
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.isPremium}
                      onChange={(e) =>
                        handleChange('isPremium', e.target.checked)
                      }
                    />
                  }
                  label="Premium Hiring client"
                />
                <Typography
                  sx={{ color: 'var(--color-text-muted)' }}
                  variant="body2"
                >
                  Premium Hiring clients post jobs without buying a plan, and appear on
                  the HR page for a dedicated HR to be assigned.
                </Typography>
              </Grid>

              {form.isPremium && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormField
                    required={false}
                    helperText="Optional label, for example Growth or Scale. Defaults to Premium Hiring."
                    label="Tier"
                    name="premiumTier"
                    value={form.premiumTier}
                    onChange={handleChange}
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button disabled={saving} onClick={handleClose}>
              Cancel
            </Button>
            <Button
              disabled={!canSubmit || saving}
              variant="contained"
              onClick={handleSubmit}
            >
              {saving ? 'Creating...' : 'Create Employer'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default EmployerCreateDialog;
